import { useMemo } from "react";
import axios from "axios";
import { getApiErrorDetail, isArt52Error } from "@/lib/api";

// Mappage canonique des erreurs API en etats UX. Factorise la logique
// de Audience.tsx / CaseDetail.tsx / Dashboard.tsx (qui faisaient le
// meme switch sur err.response.status). Pas un hook "actif" -- juste
// un classifier pur memoise sur la valeur d'erreur.
export interface ApiErrorDebug {
  /** axios error code (ERR_NETWORK, ECONNABORTED, ETIMEDOUT, ERR_BAD_REQUEST, …). */
  code?: string;
  /** Message brut de l'erreur axios / JS. */
  message?: string;
  /** Methode HTTP de la requete qui a echoue. */
  method?: string;
  /** URL complete de la requete qui a echoue. */
  url?: string;
  /** Status HTTP recu (undefined si pas de reponse — preflight CORS, timeout, DNS). */
  status?: number;
}

export type ApiErrorState =
  | { kind: "none" }
  | { kind: "unauthenticated"; detail: string; debug?: ApiErrorDebug }
  | { kind: "art52"; detail: string; debug?: ApiErrorDebug }
  | { kind: "forbidden"; detail: string; debug?: ApiErrorDebug }
  | { kind: "consent_required"; detail: string; debug?: ApiErrorDebug }
  | { kind: "not_found"; detail: string; debug?: ApiErrorDebug }
  | { kind: "unavailable"; detail: string; debug?: ApiErrorDebug }
  | { kind: "network"; detail: string; debug?: ApiErrorDebug }
  | { kind: "other"; detail: string; status?: number; debug?: ApiErrorDebug };

function buildDebug(error: unknown): ApiErrorDebug | undefined {
  if (!axios.isAxiosError(error)) {
    if (error instanceof Error) {
      return { message: error.message };
    }
    return undefined;
  }
  const fullUrl = (() => {
    const cfg = error.config;
    if (!cfg) return undefined;
    if (cfg.baseURL && cfg.url && !/^https?:/.test(cfg.url)) {
      return cfg.baseURL.replace(/\/$/, "") + cfg.url;
    }
    return cfg.url;
  })();
  return {
    code: error.code,
    message: error.message,
    method: error.config?.method?.toUpperCase(),
    url: fullUrl,
    status: error.response?.status,
  };
}

export function classifyApiError(error: unknown): ApiErrorState {
  if (error == null) return { kind: "none" };
  const detail = getApiErrorDetail(error) ?? "Erreur inattendue.";
  const debug = buildDebug(error);

  if (axios.isAxiosError(error)) {
    const code = error.response?.status;
    if (!code)
      return { kind: "network", detail: "Reseau indisponible.", debug };
    if (code === 401)
      return {
        kind: "unauthenticated",
        detail: "Authentification requise.",
        debug,
      };
    if (isArt52Error(error)) return { kind: "art52", detail, debug };
    if (code === 403) return { kind: "forbidden", detail, debug };
    if (code === 412) return { kind: "consent_required", detail, debug };
    if (code === 404) return { kind: "not_found", detail, debug };
    if (code === 503) return { kind: "unavailable", detail, debug };
    return { kind: "other", detail, status: code, debug };
  }
  return { kind: "other", detail, debug };
}

/**
 * Formate les details techniques d'une erreur API en un bloc compact
 * affichable dans l'UI. Utilise pour debug live quand le classifier
 * dit `kind:"network"` (cas typiques : preflight CORS bloque, ITP
 * Safari, DNS fail, timeout) ou pour tout autre cas opaque.
 */
export function formatApiDebug(debug: ApiErrorDebug | undefined): string {
  if (!debug) return "";
  const parts: string[] = [];
  if (debug.method && debug.url) parts.push(`${debug.method} ${debug.url}`);
  else if (debug.url) parts.push(debug.url);
  if (debug.status !== undefined) parts.push(`HTTP ${debug.status}`);
  if (debug.code) parts.push(`code=${debug.code}`);
  if (debug.message) parts.push(debug.message);
  return parts.join(" · ");
}

export function useApiErrorState(error: unknown): ApiErrorState {
  return useMemo(() => classifyApiError(error), [error]);
}
